import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Phone, MessageCircle, MapPin, ArrowRight, Camera, Video, Mic, Radio, Sparkles, Award } from "lucide-react";
import { api } from "@/lib/api";
import { mapsUrl } from "@/lib/maps";
import { CoverMedia } from "@/components/CoverMedia";
import { useHomeLayout, sortFeatured } from "@/components/HomeLayoutEditor";
import { Testimonials } from "@/components/Testimonials";

const WHATSAPP = "15615037743";
const PHONE = "+15615037743";

const fade = {
  hidden: { opacity: 0, y: 28 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.08 } }),
};

const WHY = [
  { icon: Award, title: "Studio-Grade Quality", text: "Cinema cameras, pro lighting and a treated room built for flawless output." },
  { icon: Sparkles, title: "Full Creative Service", text: "From concept to final cut — photo, video, audio and strategy under one roof." },
  { icon: Radio, title: "South Florida's Home", text: "Centrally located in Boynton Beach, serving artists & brands across the region." },
];

export default function Home() {
  const { data: services = [] } = useQuery({ queryKey: ["services"], queryFn: () => api.get("/services").then((r) => r.data) });
  const { data: portfolio = [] } = useQuery({ queryKey: ["portfolio"], queryFn: () => api.get("/portfolio").then((r) => r.data) });
  const featured = sortFeatured(portfolio).slice(0, 4);
  const { data: layout = DEFAULT_LAYOUT } = useHomeLayout();

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-[100svh] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1512790182412-b19e6d62bc39?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600')" }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/60 to-[#050505]" />
        <motion.div
          initial="hidden"
          animate="show"
          className="relative z-10 text-center px-5 pt-20"
        >
          <motion.img
            variants={fade}
            src="/xmd-logo.png"
            alt="XMD Productions"
            className="w-full max-w-[560px] mx-auto drop-shadow-[0_8px_40px_rgba(0,0,0,0.6)]"
          />
          <motion.p variants={fade} custom={1} className="mt-6 text-white/70 text-base md:text-lg max-w-xl mx-auto">
            South Florida's premium media production studio — photography, video, podcast, livestream & studio rental.
          </motion.p>
          <motion.div variants={fade} custom={2} className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Link to="/book" data-testid="hero-book-btn" className="btn-gold rounded-full px-8 py-3 uppercase tracking-wide font-mono-label text-sm">
              Book a Session
            </Link>
            <Link to="/portfolio" data-testid="hero-portfolio-btn" className="rounded-full px-8 py-3 uppercase tracking-wide font-mono-label text-sm border border-white/20 text-white hover:border-[#D4AF37] transition-colors">
              View Our Work
            </Link>
          </motion.div>

          {/* CONTACT CHIPS */}
          <motion.div variants={fade} custom={3} className="mt-12 flex flex-wrap items-center justify-center gap-3">
            <a href={`tel:${PHONE}`} data-testid="chip-call" className="glass rounded-full px-5 py-2.5 flex items-center gap-2 text-sm text-white hover:border-[#D4AF37]/50 transition-colors">
              <Phone size={16} className="text-[#D4AF37]" /> Call
            </a>
            <a href={`https://wa.me/${WHATSAPP}`} target="_blank" rel="noreferrer" data-testid="chip-whatsapp" className="glass rounded-full px-5 py-2.5 flex items-center gap-2 text-sm text-white hover:border-[#D4AF37]/50 transition-colors">
              <MessageCircle size={16} className="text-[#D4AF37]" /> WhatsApp
            </a>
            <a href={mapsUrl()} target="_blank" rel="noreferrer" data-testid="chip-directions" className="glass rounded-full px-5 py-2.5 flex items-center gap-2 text-sm text-white hover:border-[#D4AF37]/50 transition-colors">
              <MapPin size={16} className="text-[#D4AF37]" /> Directions
            </a>
          </motion.div>
        </motion.div>
      </section>

      {layout.filter((x) => x.visible).map((x) => {
        const Section = SECTIONS[x.key];
        return Section ? <div key={x.key} data-testid={`home-block-${x.key}`}><Section services={services} featured={featured} /></div> : null;
      })}
    </div>
  );
}

function SectionLabel({ children, center }) {
  return (
    <p className={`font-mono-label uppercase tracking-[0.3em] text-xs text-[#D4AF37] ${center ? "text-center" : ""}`}>
      {children}
    </p>
  );
}

function WhySection() {
  return (
      <section className="max-w-6xl mx-auto px-5 py-24">
        <SectionLabel>Why XMD</SectionLabel>
        <h2 className="font-display text-4xl md:text-5xl text-white mt-3 mb-12 max-w-2xl">
          A studio obsessed with <span className="gold-text">cinematic quality.</span>
        </h2>
        <div className="grid md:grid-cols-3 gap-5">
          {WHY.map((w, i) => (
            <motion.div
              key={w.title}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              variants={fade}
              custom={i}
              className="bg-[#111] border border-white/10 rounded-2xl p-7 hover:border-[#D4AF37]/40 transition-colors"
              data-testid={`why-card-${i}`}
            >
              <w.icon className="text-[#D4AF37] mb-4" size={28} />
              <h3 className="font-display text-xl text-white mb-2">{w.title}</h3>
              <p className="text-white/55 text-sm leading-relaxed">{w.text}</p>
            </motion.div>
          ))}
        </div>
      </section>
  );
}

function ServicesSection({ services }) {
  return (
      <section className="max-w-6xl mx-auto px-5 py-16">
        <div className="flex items-end justify-between mb-10">
          <div>
            <SectionLabel>What We Do</SectionLabel>
            <h2 className="font-display text-4xl md:text-5xl text-white mt-3">Our Services</h2>
          </div>
          <Link to="/services" data-testid="services-view-all" className="hidden md:flex items-center gap-2 text-[#D4AF37] text-sm uppercase font-mono-label tracking-wide">
            All Services <ArrowRight size={16} />
          </Link>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {services.slice(0, 6).map((s, i) => (
            <Link key={s.id} to={`/services/${s.slug}`} data-testid={`service-preview-${i}`}>
              <motion.div
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
                variants={fade}
                custom={i}
                className="group relative rounded-2xl overflow-hidden border border-white/10 h-72"
              >
                <div className="absolute inset-0"><CoverMedia item={s} /></div>
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                <div className="absolute bottom-0 p-5 w-full">
                  <p className="font-mono-label uppercase text-[10px] tracking-[0.2em] text-[#D4AF37]">{s.category}</p>
                  <h3 className="font-display text-xl text-white mt-1">{s.title}</h3>
                  <p className="text-[#D4AF37] font-semibold text-sm mt-1">{s.price}</p>
                </div>
              </motion.div>
            </Link>
          ))}
        </div>
      </section>
  );
}

function FeaturedSection({ featured }) {
  if (!featured.length) return null;
  return (
        <section className="max-w-6xl mx-auto px-5 py-16">
          <SectionLabel>Featured</SectionLabel>
          <h2 className="font-display text-4xl md:text-5xl text-white mt-3 mb-10">Recent Productions</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {featured.map((p, i) => (
              <Link key={p.id} to={`/portfolio/${p.slug}`} data-testid={`featured-${i}`}>
                <motion.div
                  initial="hidden"
                  whileInView="show"
                  viewport={{ once: true }}
                  variants={fade}
                  custom={i}
                  className="group relative rounded-xl overflow-hidden border border-white/10 aspect-[3/4]"
                >
                  <div className="absolute inset-0"><CoverMedia item={p} playSize="sm" /></div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 to-transparent opacity-80" />
                  <span className="absolute top-3 left-3 bg-[#D4AF37] text-black text-[9px] font-bold uppercase tracking-wide px-2 py-1 rounded">Featured</span>
                  <div className="absolute bottom-0 p-4">
                    <h3 className="font-display text-sm text-white leading-tight">{p.title}</h3>
                  </div>
                </motion.div>
              </Link>
            ))}
          </div>
        </section>
  );
}

function CtaSection() {
  return (
      <section className="max-w-6xl mx-auto px-5 py-24">
        <div className="relative rounded-3xl overflow-hidden border border-[#D4AF37]/20 bg-gradient-to-br from-[#161310] to-[#0a0a0a] p-10 md:p-16 text-center">
          <SectionLabel center>Let's Create</SectionLabel>
          <h2 className="font-display text-4xl md:text-6xl text-white mt-4 mb-6">
            Ready to make <span className="gold-text">something iconic?</span>
          </h2>
          <p className="text-white/60 max-w-lg mx-auto mb-8">Book your session online in minutes. Pick a service, choose your time, and pay securely to lock it in.</p>
          <Link to="/book" data-testid="cta-book-btn" className="btn-gold inline-flex rounded-full px-10 py-4 uppercase tracking-wide font-mono-label text-sm">
            Book Your Session
          </Link>
        </div>
      </section>
  );
}

const SECTIONS = { why: WhySection, services: ServicesSection, featured: FeaturedSection, testimonials: Testimonials, cta: CtaSection };
const DEFAULT_LAYOUT = Object.keys(SECTIONS).map((key) => ({ key, visible: true }));
